import React, { useState } from 'react';
import { X, Edit, Target, Calendar, Clock, Layers, Award, Plus, Brain, User } from 'lucide-react';
import { Goal } from '../../types';
import { EnhancedGoalMetrics } from './EnhancedGoalMetrics';
import { EditGoalModal } from './EditGoalModal';
import { CreateGoalModal } from './CreateGoalModal';
import { GoalAIDecomposer } from './GoalAIDecomposer';
import ExpertProfileModal from './ExpertProfileModal';

interface GoalDetailModalProps {
  goal: Goal;
  onClose: () => void;
  onGoalUpdated: (updatedGoal: Goal) => void;
}

export const GoalDetailModal: React.FC<GoalDetailModalProps> = ({
  goal,
  onClose,
  onGoalUpdated
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateSubGoalModal, setShowCreateSubGoalModal] = useState(false);
  const [showAIDecomposeModal, setShowAIDecomposeModal] = useState(false);
  const [showExpertModal, setShowExpertModal] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'blocked':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleGoalEdited = (updatedGoal: Goal) => {
    setShowEditModal(false);
    onGoalUpdated(updatedGoal);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 pt-8">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white sticky top-0">
            <div className="flex items-center gap-3 flex-1">
              <Target className="w-6 h-6 text-blue-600" />
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  {goal.name}
                </h2>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(goal.status)}`}>
                    {formatStatus(goal.status)}
                  </span>
                  {goal.level !== undefined && (
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Layers className="w-4 h-4" />
                      Level {goal.level}
                    </div>
                  )}
                  {goal.standalonePotential && (
                    <div className="flex items-center gap-1 text-sm text-purple-600">
                      <Award className="w-4 h-4" />
                      Standalone Potential
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowCreateSubGoalModal(true)}
                className="btn-secondary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Sub-Goal
              </button>
              <button
                onClick={() => setShowExpertModal(true)}
                className="btn-secondary flex items-center gap-2"
                title="Configure expert profile for this goal"
              >
                <User className="w-4 h-4" />
                Expert Profile
              </button>
              <button
                onClick={() => setShowAIDecomposeModal(true)}
                className="btn-secondary flex items-center gap-2"
                title="AI Decompose this goal into sub-goals"
              >
                <Brain className="w-4 h-4" />
                AI Decompose
              </button>
              <button
                onClick={() => setShowEditModal(true)}
                className="btn-secondary flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit Goal
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
            <div className="p-6 space-y-6">
              {/* Goal Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Goal Information</h3>

                  <div className="space-y-4">
                    {goal.description && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <p className="text-gray-900">{goal.description}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      {goal.estimatedHours && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Estimated Hours
                          </label>
                          <div className="flex items-center gap-1 text-gray-900">
                            <Clock className="w-4 h-4" />
                            {goal.estimatedHours}h
                          </div>
                        </div>
                      )}

                      {goal.complexityScore && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Complexity Score
                          </label>
                          <div className="text-gray-900">
                            {goal.complexityScore}/10
                          </div>
                        </div>
                      )}
                    </div>

                    {goal.targetDate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Target Date
                        </label>
                        <div className="flex items-center gap-1 text-gray-900">
                          <Calendar className="w-4 h-4" />
                          {new Date(goal.targetDate).toLocaleDateString()}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Progress
                      </label>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(goal.progress, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {Math.round(goal.progress)}%
                        </span>
                      </div>
                    </div>

                    {goal.createdAt && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Created
                        </label>
                        <p className="text-gray-900">
                          {new Date(goal.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* AI Information */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Analysis</h3>

                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        goal.aiGenerated 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {goal.aiGenerated ? 'AI Generated' : 'Manual'}
                      </span>
                      {goal.standalonePotential && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Standalone Potential
                        </span>
                      )}
                    </div>

                    {goal.aiReasoning && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          AI Reasoning
                        </label>
                        <p className="text-gray-900 text-sm bg-gray-50 p-3 rounded">
                          {goal.aiReasoning}
                        </p>
                      </div>
                    )}

                    {goal.children && goal.children.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Sub-Goals
                        </label>
                        <p className="text-gray-900">
                          {goal.children.length} sub-goal{goal.children.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Enhanced Metrics Section */}
              <EnhancedGoalMetrics
                goal={goal}
                onGoalUpdated={() => {
                  // Refresh goal data if needed
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditGoalModal
          goal={goal}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleGoalEdited}
        />
      )}

      {/* Create Sub-Goal Modal */}
      {showCreateSubGoalModal && (
        <CreateGoalModal
          projectId={goal.projectId}
          parentGoal={goal}
          onClose={() => setShowCreateSubGoalModal(false)}
          onSuccess={() => {
            setShowCreateSubGoalModal(false);
            onGoalUpdated(goal); // Refresh the view
          }}
        />
      )}

      {/* Expert Profile Modal */}
      {showExpertModal && (
        <ExpertProfileModal
          isOpen={showExpertModal}
          onClose={() => setShowExpertModal(false)}
          type="goal"
          entityId={goal.id}
          entityName={goal.name}
          onSave={(expertProfile) => {
            setShowExpertModal(false);
            // Could trigger a refresh or show success message
          }}
        />
      )}

      {/* AI Decompose Modal */}
      {showAIDecomposeModal && (
        <GoalAIDecomposer
          goal={goal}
          onClose={() => setShowAIDecomposeModal(false)}
          onSuccess={() => {
            setShowAIDecomposeModal(false);
            onGoalUpdated(goal); // Refresh the goal view
          }}
        />
      )}
    </div>
  );
};
